import { useCallback, useEffect, useRef } from 'react';

const SERVICE_NOW_LINK_POLL_MS = 3000;
const SERVICE_NOW_LINK_MAX_POLL_MS = 120_000;

export type ServiceNowSyncableTicketType = 'INCIDENT' | 'CHANGE' | 'SERVICE_REQUEST';

interface TicketServiceNowFields {
  serviceNowLinked?: boolean;
  serviceNowKnown?: boolean;
  ticketType?: string;
  createdAt?: string;
}

/** Minimal query shape accepted by React Query refetchInterval callbacks. */
export interface RefetchIntervalQueryLike {
  state: {
    data?: unknown;
  };
}

export type ServiceNowLinkRefetchIntervalFn = (
  query: RefetchIntervalQueryLike
) => number | false;

export function isServiceNowSyncableTicketType(
  ticketType?: string
): ticketType is ServiceNowSyncableTicketType {
  return (
    ticketType === 'INCIDENT' ||
    ticketType === 'CHANGE' ||
    ticketType === 'SERVICE_REQUEST'
  );
}

function asTicketServiceNowFields(data: unknown): TicketServiceNowFields | undefined {
  if (data == null || typeof data !== 'object') {
    return undefined;
  }
  return data as TicketServiceNowFields;
}

/**
 * Returns a React Query refetchInterval callback that polls until async
 * ServiceNow outbound sync creates the ExternalTicketLink on the ticket.
 * Only polls recently created tickets — older tickets without a link are left alone.
 */
export function useServiceNowLinkRefetchInterval(
  ticketId?: string,
  fallbackTicketType?: ServiceNowSyncableTicketType
): ServiceNowLinkRefetchIntervalFn {
  const pollStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    pollStartedAtRef.current = null;
  }, [ticketId]);

  return useCallback(
    (query: RefetchIntervalQueryLike): number | false => {
      const ticket = asTicketServiceNowFields(query.state.data);
      const ticketType = ticket?.ticketType ?? fallbackTicketType;
      if (!ticket || !isServiceNowSyncableTicketType(ticketType)) {
        pollStartedAtRef.current = null;
        return false;
      }

      if (ticket.serviceNowLinked || ticket.serviceNowKnown) {
        pollStartedAtRef.current = null;
        return false;
      }

      // Don't hammer detail GET for historical tickets that were never linked to ServiceNow
      if (ticket.createdAt) {
        const createdMs = new Date(ticket.createdAt).getTime();
        if (Number.isFinite(createdMs) && Date.now() - createdMs > SERVICE_NOW_LINK_MAX_POLL_MS) {
          pollStartedAtRef.current = null;
          return false;
        }
      }

      if (pollStartedAtRef.current === null) {
        pollStartedAtRef.current = Date.now();
      }

      if (Date.now() - pollStartedAtRef.current >= SERVICE_NOW_LINK_MAX_POLL_MS) {
        return false;
      }

      return SERVICE_NOW_LINK_POLL_MS;
    },
    [fallbackTicketType]
  );
}
